import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { auth, firestore } from '../firebase';
import { fetchBooks } from '../utils/bookService';
import { loadBingoGrid, loadUserProgress, saveFinishedBook, saveUserProgress, checkSelectedBookNeedsToBeSaved } from '../utils/firebaseUtils';
import BingoCell from '../components/BingoCell';
import Leaderboard from '../components/Leaderboard';
import Navigation from './Navigation';
import { useNavigate, useLocation } from 'react-router-dom';
import '../css/Bingo.css';
import { doc, updateDoc, increment, onSnapshot, deleteDoc, collection, query, where, getDocs, getDoc, orderBy } from 'firebase/firestore';
import { showSuccessToast, showErrorToast, showInfoToast } from '../utils/toastUtils';
import { rateLimiter } from '../utils/simpleRateLimiter';
import { handleGuestNavigation } from '../utils/navigationUtils';
import { useAuth } from '../AuthContext';
import BlogReviewModal from './BlogReviewModal';
import { addDoc } from 'firebase/firestore';

const Bingo = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { canAccess, user } = useAuth();
    const [gridTitle, setGridTitle] = useState("");
    const [bingoCells, setBingoCells] = useState([]);
    const [userProgress, setUserProgress] = useState([]);
    const [searchOverlay, setSearchOverlay] = useState({ visible: false, index: null });
    const [searchQuery, setSearchQuery] = useState("");
    const [searchResults, setSearchResults] = useState([]);
    const [leaderboardVisible, setLeaderboardVisible] = useState(false);
    const [userPoints, setUserPoints] = useState(0);
    const pointsListenerRef = useRef(null);
    const [isProcessing, setIsProcessing] = useState(false);
    const processingTimeoutRef = useRef(null);
    const processingCellRef = useRef(null);
    const [processingBookId, setProcessingBookId] = useState(null);
    const [wildcardBook, setWildcardBook] = useState(null);
    const [showReviewModal, setShowReviewModal] = useState(false);
    const [reviewBook, setReviewBook] = useState(null);
    const [resetDate, setResetDate] = useState(null);
    const [daysLeft, setDaysLeft] = useState("");
    // New state variables for grid selection
    const [availableGrids, setAvailableGrids] = useState([]);
    const [currentGridId, setCurrentGridId] = useState("xinroimX7dzKX1ZWX4m9");
    const [isHistoricalGrid, setIsHistoricalGrid] = useState(false);
    // Add this state for points animation
    const [pointsAnimation, setPointsAnimation] = useState(false);

    // Constants for cache durations
    const GRID_CACHE_DURATION = 30 * 24 * 60 * 60 * 1000; // 30 days in milliseconds
    const PROGRESS_CACHE_DURATION = 5 * 60 * 1000; // 5 minutes in milliseconds

    // Helper function for error handling and cleanup
    const failAndStop = useCallback((message) => {
        showErrorToast(message);
        setIsProcessing(false);
        processingCellRef.current = null;
    }, []);

    // Function to fetch all available grids
    const fetchAvailableGrids = useCallback(async () => {
        try {
            const gridsQuery = query(collection(firestore, "BingoGrid"), orderBy("Timestamp", "desc"));
            const gridsSnapshot = await getDocs(gridsQuery);
            const grids = gridsSnapshot.docs.map(doc => ({
                id: doc.id,
                title: doc.data().Title,
                timestamp: doc.data().Timestamp.toDate()
            }));
            setAvailableGrids(grids);
        } catch (error) {
            console.error("Error fetching available grids:", error);
            showErrorToast("Failed to load available grids");
        }
    }, []);

    // Memoize the grid data loading function
    const loadGridData = useCallback(async () => {
        if (!user) return;

        if (!rateLimiter.canMakeRequest(user.uid)) {
            showErrorToast("Too many requests. Please wait a moment before trying again.");
            return;
        }

        try {
            const cachedGridData = localStorage.getItem(`bingoGridData_${currentGridId}`);
            const cachedTimestamp = localStorage.getItem(`bingoGridTimestamp_${currentGridId}`);

            // Get the grid data directly
            const gridData = await loadBingoGrid(currentGridId);
            
            // Check if we have cached data and if it's still valid
            if (cachedGridData && cachedTimestamp && 
                Date.now() - parseInt(cachedTimestamp) < GRID_CACHE_DURATION) {
                const { title, cells } = JSON.parse(cachedGridData);
                setGridTitle(title);
                setBingoCells(cells);
            } else {
                setGridTitle(gridData.title);
                setBingoCells(gridData.cells);
                
                localStorage.setItem(`bingoGridData_${currentGridId}`, JSON.stringify(gridData));
                localStorage.setItem(`bingoGridTimestamp_${currentGridId}`, Date.now().toString());
            }

            // Set whether this is a historical grid
            setIsHistoricalGrid(currentGridId !== "xinroimX7dzKX1ZWX4m9");
        } catch (error) {
            console.error("Error loading grid data:", error);
            showErrorToast(error.message);
        }
    }, [GRID_CACHE_DURATION, user, currentGridId]);

    // Load available grids on component mount
    useEffect(() => {
        fetchAvailableGrids();
    }, [fetchAvailableGrids]);

    // Handle grid selection change
    const handleGridChange = useCallback((event) => {
        const newGridId = event.target.value;
        setCurrentGridId(newGridId);
    }, []);

    // Update the points listener effect to include animation
    useEffect(() => {
        let isMounted = true;
        let unsubscribe = null;

        const setupPointsListener = async () => {
            if (!user) {
                setUserPoints(0);
                return;
            }

            if (unsubscribe) {
                unsubscribe();
                unsubscribe = null;
            }

            try {
                const userRef = doc(firestore, "users", user.uid);
                unsubscribe = onSnapshot(userRef, (doc) => {
                    if (!isMounted) return;
                    
                    if (doc.exists()) {
                        const points = doc.data()?.points || 0;
                        if (points !== userPoints) {
                            setPointsAnimation(true);
                            setTimeout(() => setPointsAnimation(false), 500);
                        }
                        setUserPoints(points);
                    }
                });

                pointsListenerRef.current = unsubscribe;
            } catch (error) {
                console.error("Error setting up points listener:", error);
                showErrorToast("Failed to load points. Please refresh the page.");
            }
        };

        setupPointsListener();

        return () => {
            isMounted = false;
            if (unsubscribe) {
                unsubscribe();
                unsubscribe = null;
            }
            if (pointsListenerRef.current) {
                pointsListenerRef.current();
                pointsListenerRef.current = null;
            }
        };
    }, [user, userPoints]);

    // Memoize the user data loading function
    const loadUserData = useCallback(async () => {
        if (!user) {
            setUserProgress([]);
            return;
        }

        try {
            const cachedProgress = localStorage.getItem(`userProgress_${user.uid}`);
            const cachedTimestamp = localStorage.getItem(`userProgressTimestamp_${user.uid}`);
            
            let progress;
            if (cachedProgress && cachedTimestamp && 
                Date.now() - parseInt(cachedTimestamp) < PROGRESS_CACHE_DURATION) {
                progress = JSON.parse(cachedProgress);
            } else {
                progress = await loadUserProgress();
                localStorage.setItem(`userProgress_${user.uid}`, JSON.stringify(progress));
                localStorage.setItem(`userProgressTimestamp_${user.uid}`, Date.now().toString());
            }
            
            setUserProgress(progress);
        } catch (error) {
            console.error("Error loading user data:", error);
            showErrorToast(error.message);
        }
    }, [PROGRESS_CACHE_DURATION, user]);

    // Update cache when user progress changes
    useEffect(() => {
        if (user && userProgress.length > 0) {
            localStorage.setItem(`userProgress_${user.uid}`, JSON.stringify(userProgress));
            localStorage.setItem(`userProgressTimestamp_${user.uid}`, Date.now().toString());
        }
    }, [userProgress, user]);

    // Initial data loading
    useEffect(() => {
        if (user) {
            loadGridData();
            loadUserData();
        }
    }, [loadGridData, loadUserData, user]);

    useEffect(() => {
        let isMounted = true;

        const handleSearchState = async () => {
            if (!location.state?.showSearch) return;

            const { searchQuery: newQuery, searchResults: newResults, selectedCellIndex } = location.state;
            
            // Only update state if values have changed
            if (newQuery !== undefined && newQuery !== searchQuery) {
                setSearchQuery(newQuery);
            }
            if (newResults !== undefined && newResults !== searchResults) {
                setSearchResults(newResults);
            }
            if (selectedCellIndex !== undefined) {
                setSearchOverlay(prev => {
                    if (prev.index === selectedCellIndex && prev.visible) return prev;
                    return { visible: true, index: selectedCellIndex };
                });
            }

            // Clear location state if needed
            if (isMounted && location.state?.showSearch) {
                navigate(location.pathname, { replace: true, state: {} });
            }
        };

        handleSearchState();

        return () => {
            isMounted = false;
        };
    }, [location.state, location.pathname, navigate, searchQuery, searchResults]);

    // Memoize the checkForLines function
    const checkForLines = useCallback((progress, index) => {
        const row = Math.floor(index / 4);
        const col = index % 4;
        let points = 0;

        // Helper function to check if a line was JUST completed by this move
        const wasLineJustCompleted = (cells) => {
            // First check if all cells in the line are filled
            const isLineComplete = cells.every(cellId => 
                progress.some(p => p.Cell_ID === cellId)
            );

            if (!isLineComplete) return false;

            // Then verify that this was the last cell needed
            // by checking if removing this cell would break the line
            const currentCellId = bingoCells[index].id;
            const otherCells = cells.filter(id => id !== currentCellId);
            const werePreviousCellsFilled = otherCells.every(cellId =>
                progress.some(p => p.Cell_ID === cellId)
            );

            // Only return true if the line is complete AND this was the last cell needed
            return isLineComplete && werePreviousCellsFilled;
        };

        // Get the cells for each possible line
        const rowCells = [0, 1, 2, 3].map(c => bingoCells[row * 4 + c].id);
        const colCells = [0, 1, 2, 3].map(r => bingoCells[r * 4 + col].id);
        const diagonal1 = [0, 5, 10, 15].map(i => bingoCells[i].id);
        const diagonal2 = [3, 6, 9, 12].map(i => bingoCells[i].id);

        // Check horizontal line
        if (wasLineJustCompleted(rowCells)) {
            points += 50;
        }

        // Check vertical line
        if (wasLineJustCompleted(colCells)) {
            points += 50;
        }

        // Only check diagonals if this cell is actually part of a diagonal
        const isOnDiagonal1 = [0, 5, 10, 15].includes(index);
        const isOnDiagonal2 = [3, 6, 9, 12].includes(index);

        if (isOnDiagonal1 && wasLineJustCompleted(diagonal1)) {
            points += 50;
        }

        if (isOnDiagonal2 && wasLineJustCompleted(diagonal2)) {
            points += 50;
        }

        // Full grid bonus
        if (progress.length === 16) points += 100;

        return points;
    }, [bingoCells]);

    // Memoize the selectBook function
    const selectBook = useCallback(async (book, index) => {
        // If we're already processing this exact cell and book, ignore the click
        if (isProcessing && processingCellRef.current === index && processingBookId === book.id) {
            return;
        }
        
        if (!auth.currentUser || auth.currentUser.isAnonymous) {
            return failAndStop('You must be logged in to save progress. Please sign in or create an account.');
        }

        if (!rateLimiter.canMakeRequest(auth.currentUser.uid)) {
            return failAndStop("Too many requests. Please wait a moment before trying again.");
        }

        setIsProcessing(true);
        processingCellRef.current = index; // Store the cell being processed
        setProcessingBookId(book.id); // Store the book being processed

        const userId = auth.currentUser.uid;
        const isbn = book.volumeInfo.industryIdentifiers?.[0]?.identifier || '';
        const cell = bingoCells[index];
        const bookTitle = book.volumeInfo.title;

        if (!isbn) {
            return failAndStop('This book does not have a valid ISBN.');
        }

        try {
            const existingProgress = userProgress.find(p => p.Cell_ID === cell.id);
            const bookId = await checkSelectedBookNeedsToBeSaved(book, isbn);

            // Count how many times this book is already used
            const timesUsed = userProgress.filter(p => p.Book_ID === bookId).length;

            // Prevent using the same book more than twice (normal + wildcard)
            if (timesUsed >= 2) {
                return failAndStop('This book has already been used twice — once normally and once as your wildcard.');
            }

            // If the book is used once
            if (timesUsed === 1) {
                // If wildcard is already set, don't allow this book to be used again
                if (wildcardBook) {
                    return failAndStop('You have already used your wildcard. Each book can only be used once, and only one wildcard is allowed.');
                } else {
                    // First time wildcard is used — allow and set
                    setWildcardBook({ Book_ID: bookId, Book_Title: bookTitle });
                }
            }

            await saveUserProgress(cell.id, bookId, bookTitle);
            const result = await saveFinishedBook(userId, bookId, bookTitle);

            if (!result.success) {
                return failAndStop('Failed to save book.');
            }

            if (!existingProgress) {
                const newProgress = [...userProgress, { Cell_ID: cell.id, Book_ID: bookId, Book_Title: bookTitle }];
                let earnedPoints = 10; // Base points
                earnedPoints += checkForLines(newProgress, index);

                const userRef = doc(firestore, "users", userId);
                await updateDoc(userRef, {
                    points: increment(earnedPoints)
                });

                showSuccessToast(`You earned ${earnedPoints} points!`);
            } else {
                showInfoToast('Book updated! (No points awarded for replacing books)');
            }

            setUserProgress(prev => [
                ...prev.filter(p => p.Cell_ID !== cell.id),
                { Cell_ID: cell.id, Book_ID: bookId, Book_Title: bookTitle }
            ]);
            setSearchOverlay({ visible: false, index: null });

            // Show the blog review modal after adding a book
            setReviewBook({
                id: bookId,
                title: bookTitle,
                coverUrl: book.volumeInfo.imageLinks?.thumbnail || 'placeholder-image.jpg',
            });
            setShowReviewModal(true);
        } catch (error) {
            console.error('Error saving book:', error);
            failAndStop(error.message);
        } finally {
            setIsProcessing(false);
            processingCellRef.current = null;
            setProcessingBookId(null);
        }
    }, [bingoCells, userProgress, checkForLines, isProcessing, wildcardBook, failAndStop, processingBookId]);

    // Cleanup timeout on unmount
    useEffect(() => {
        const timeoutRef = processingTimeoutRef.current;
        return () => {
            if (timeoutRef) {
                clearTimeout(timeoutRef);
            }
        };
    }, []);

    // Memoize the handleSearch function
    const handleSearch = useCallback(async () => {
        if (!searchQuery.trim()) return;
        
        try {
            const formattedQuery = `q=${encodeURIComponent(searchQuery)}`;
            const results = await fetchBooks(formattedQuery, user?.uid);
            setSearchResults(results);
        } catch (error) {
            console.error("Error searching for books:", error);
            showErrorToast(error.message);
        }
    }, [searchQuery, user]);

    // Memoize the handleViewBook function
    const handleViewBook = useCallback((book) => {
        const isbn = book.volumeInfo.industryIdentifiers?.[0]?.identifier;
        if (isbn) {
            if (!handleGuestNavigation(canAccess, `/book/${isbn}`, navigate, { 
                state: { 
                    searchQuery,
                    searchResults,
                    selectedCellIndex: searchOverlay.index
                }
            })) {
                return;
            }
        } else {
            showErrorToast('This book does not have a valid ISBN.');
        }
    }, [canAccess, navigate, searchOverlay.index, searchQuery, searchResults]);

    // Memoize the cell click handler
    const handleCellClick = useCallback((index) => {
        if (isHistoricalGrid) {
            showInfoToast("You cannot modify historical grids");
            return;
        }
        setSearchOverlay({ visible: true, index });
    }, [isHistoricalGrid]);

    // Memoize the BingoCell components
    const renderedCells = useMemo(() => 
        bingoCells.map((cell, index) => (
            <BingoCell
                key={cell.id}
                cell={cell}
                userProgress={userProgress}
                onClick={() => handleCellClick(index)}
                isHistorical={isHistoricalGrid}
            />
        )), [bingoCells, userProgress, handleCellClick, isHistoricalGrid]);

    // Add clearGrid function
    const clearGrid = useCallback(async () => {
        if (!auth.currentUser || auth.currentUser.isAnonymous) {
            showErrorToast('You must be logged in to clear your grid.');
            return;
        }

        if (!rateLimiter.canMakeRequest(auth.currentUser.uid)) {
            showErrorToast("Too many requests. Please wait a moment before trying again.");
            return;
        }

        try {
            const userId = auth.currentUser.uid;
            
            // Clear user progress from Firestore
            const progressRef = collection(firestore, "UserProgress");
            const q = query(progressRef, where("User_ID", "==", userId));
            const querySnapshot = await getDocs(q);
            
            const deletePromises = querySnapshot.docs.map(doc => deleteDoc(doc.ref));
            await Promise.all(deletePromises);

            // Reset points to 0
            const userRef = doc(firestore, "users", userId);
            await updateDoc(userRef, {
                points: 0
            });

            // Clear local cache
            localStorage.removeItem(`userProgress_${userId}`);
            localStorage.removeItem(`userProgressTimestamp_${userId}`);
            localStorage.removeItem(`bingoGridData_${currentGridId}`);
            localStorage.removeItem(`bingoGridTimestamp_${currentGridId}`);

            // Update local state
            setUserProgress([]);
            setUserPoints(0);
            setWildcardBook(null); // Reset wildcard book

            showSuccessToast('Grid cleared successfully!');
        } catch (error) {
            console.error('Error clearing grid:', error);
            showErrorToast(error.message);
        }
    }, [currentGridId]);

    // Add this function to handle blog review submission
    const handleBlogReviewSubmit = async (reviewData) => {
        try {
            await addDoc(collection(firestore, 'BlogPost'), {
                ...reviewData,
                timestamp: new Date()
            });
            showSuccessToast('Your review has been posted to the blog!');
        } catch (error) {
            showErrorToast('Failed to post review. Please try again.');
        }
        setShowReviewModal(false);
        setReviewBook(null);
    };

    // Fetch reset date from Firestore
    useEffect(() => {
        const fetchResetDate = async () => {
            const docRef = doc(firestore, "settings", "bingoReset");
            const docSnap = await getDoc(docRef);
            if (docSnap.exists() && docSnap.data().resetDate) {
                const date = docSnap.data().resetDate.toDate();
                setResetDate(date);
            }
        };
        fetchResetDate();
    }, []);

    // Calculate days left when resetDate changes
    useEffect(() => {
        if (!resetDate) return;
        const calculateDaysLeft = () => {
            const now = new Date();
            const difference = resetDate - now;
            if (difference <= 0) return "Time's up!";
            const days = Math.ceil(difference / (1000 * 60 * 60 * 24));
            return `${days} days left`;
        };
        setDaysLeft(calculateDaysLeft());
        const timer = setInterval(() => setDaysLeft(calculateDaysLeft()), 24 * 60 * 60 * 1000);
        return () => clearInterval(timer);
    }, [resetDate]);

    return (
        <div className="bingo-container">
            <Navigation />
            <div className="bingo-content">
                <h1 className="title">{gridTitle}</h1>
                {!isHistoricalGrid && (
                    <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', marginBottom: 10 }}>
                        <div className="points-tracker">
                            Points: <span className={`points-value ${pointsAnimation ? 'animate' : ''}`}>{userPoints}</span>
                        </div>
                        {resetDate && (
                            <div className="points-tracker">
                                <span className="points-value days-left">{daysLeft}</span>
                            </div>
                        )}
                    </div>
                )}
                <div className="grid-selector">
                    <select 
                        value={currentGridId} 
                        onChange={handleGridChange}
                        className="grid-select"
                    >
                        {availableGrids.map(grid => (
                            <option key={grid.id} value={grid.id}>
                                {grid.title} ({grid.timestamp.toLocaleDateString()})
                            </option>
                        ))}
                    </select>
                </div>

                {isHistoricalGrid && (
                    <div className="past-challenge-indicator">
                        Past Challenge
                    </div>
                )}

                <div className={`bingo-grid ${isHistoricalGrid ? 'historical' : ''}`}>
                    {renderedCells}
                </div>

                {searchOverlay.visible && !isHistoricalGrid && (
                    <div className="search-overlay">
                        <div className="search-modal">
                            <h2>{bingoCells[searchOverlay.index]?.Challenge || 'Select a Book'}</h2>
                            <div className="search-controls">
                                <input 
                                    type="text" 
                                    placeholder="Search for books..." 
                                    value={searchQuery} 
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                                />
                                <button onClick={handleSearch}>Search</button>
                                <button onClick={() => setSearchOverlay({ visible: false, index: null })}>Close</button>
                            </div>
                            
                            <div className="search-results">
                                {searchResults.length > 0 ? (
                                    searchResults.map((book) => (
                                        <div 
                                            key={book.id} 
                                            className="book-result"
                                        >
                                            <div 
                                                className={`book-result-content ${isProcessing && processingCellRef.current === searchOverlay.index && processingBookId === book.id ? 'processing' : ''}`} 
                                                onClick={() => selectBook(book, searchOverlay.index)}
                                            >
                                                <img 
                                                    src={book.volumeInfo.imageLinks?.thumbnail || 'placeholder-image.jpg'} 
                                                    alt={book.volumeInfo.title}
                                                />
                                                <div className="book-info">
                                                    <h3>{book.volumeInfo.title}</h3>
                                                    <p>{book.volumeInfo.authors?.join(', ')}</p>
                                                </div>
                                            </div>
                                            <button 
                                                className="view-book-btn"
                                                onClick={() => handleViewBook(book)}
                                                disabled={isProcessing && processingCellRef.current === searchOverlay.index && processingBookId === book.id}
                                            >
                                                View Book
                                            </button>
                                        </div>
                                    ))
                                ) : (
                                    <p className="no-results">No books found. Try a different search term.</p>
                                )}
                            </div>
                        </div>
                    </div>
                )}

                <div className="leaderboard-btn-container">
                    <button 
                        className="leaderboard-btn" 
                        onClick={() => setLeaderboardVisible(true)}
                    >
                        View Leaderboard
                    </button>
                    {!isHistoricalGrid && (
                        <button 
                            className="reset-grid-btn" 
                            onClick={clearGrid}
                        >
                            Reset Grid
                        </button>
                    )}
                </div>

                {leaderboardVisible && (
                    <Leaderboard onClose={() => setLeaderboardVisible(false)} />
                )}

                {showReviewModal && reviewBook && (
                    <BlogReviewModal
                        book={reviewBook}
                        user={user}
                        onSubmit={handleBlogReviewSubmit}
                        onClose={() => { setShowReviewModal(false); setReviewBook(null); }}
                    />
                )}
            </div>
        </div>
    );
};

export default Bingo;
